import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogo, Envelope, Lock, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const { login, register, loginGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const invitedEmail = params.get("email");
  const from = location.state?.from || (inviteToken ? `/org/join?token=${inviteToken}` : "/dashboard");

  const [mode, setMode] = useState(inviteToken ? "register" : "login");
  const [email, setEmail] = useState(invitedEmail || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invitedEmail) setEmail(invitedEmail);
  }, [invitedEmail]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name, inviteToken);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created.");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-wide py-12 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
            {mode === "login" ? "Welcome back" : "Create account"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter">
            {mode === "login" ? "Sign in to Ordeneli." : "Start building in seconds."}
          </h1>
          <p className="mt-4 sm:mt-5 text-[#525252] text-base sm:text-lg max-w-md">
            Ten AI marketing tools in one studio. Every generation is saved to your private history.
          </p>
          <div className="mt-8 border-t border-[#E5E7EB] pt-5 text-sm text-[#525252]">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mb-2">Demo credentials</p>
            <p><strong>demo@ordeneli.ai</strong> · <code className="font-mono">DemoOrdeneli2026!</code></p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="w-full max-w-md border border-[#E5E7EB] bg-white p-6 sm:p-8">
            <button
              onClick={loginGoogle}
              className="w-full border border-[#0A0A0A] py-3 px-4 flex items-center justify-center gap-3 font-bold hover:bg-[#0A0A0A] hover:text-white transition-all"
              data-testid="google-login-btn"
            >
              <GoogleLogo size={20} weight="bold" />
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#E5E7EB]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">or email</span>
              <div className="flex-1 h-px bg-[#E5E7EB]" />
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="auth-form">
              {mode === "register" && (
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold">Name</span>
                  <input
                    className="input-line"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="auth-name"
                  />
                </label>
              )}
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                  <Envelope size={14} weight="bold" /> Email
                </span>
                <input
                  className="input-line"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  data-testid="auth-email"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold inline-flex items-center justify-between gap-1.5">
                  <span className="inline-flex items-center gap-1.5"><Lock size={14} weight="bold" /> Password</span>
                  {mode === "login" && (
                    <Link to="/auth/forgot" className="text-xs font-semibold text-[#002FA7] hover:underline" data-testid="forgot-link">
                      Forgot?
                    </Link>
                  )}
                </span>
                <input
                  className="input-line"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  data-testid="auth-password"
                />
              </label>
              <button type="submit" disabled={loading} className="btn-primary justify-center mt-2" data-testid="auth-submit">
                {loading ? "…" : mode === "login" ? "Sign in" : "Create account"}
                <ArrowRight size={16} weight="bold" />
              </button>
            </form>

            <p className="mt-6 text-sm text-[#525252] text-center">
              {mode === "login" ? (
                <>New here?{" "}
                  <button onClick={() => setMode("register")} className="font-bold text-[#002FA7]" data-testid="switch-to-register">
                    Create an account
                  </button>
                </>
              ) : (
                <>Already have an account?{" "}
                  <button onClick={() => setMode("login")} className="font-bold text-[#002FA7]" data-testid="switch-to-login">
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
          <p className="mt-4 text-xs text-[#737373] max-w-md">
            By continuing you agree to our <Link to="/" className="underline">terms</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
